/**
 * commandSources — statische Indizes für die Command-Palette (⌘K / Strg-K).
 *
 * Diese Datei ist bewusst React-frei: reine Datenlisten + ein Doku-Index-
 * Builder. Alle drei Quellen (Seiten, Aktionen, Dokumentation) werden in der
 * Palette client-seitig, synchron und ohne Netzwerk gefiltert (Scoring).
 * Nur die Mitarbeiter-Suche geht debounced an den Server (siehe CommandPalette).
 */

import {
  BookOpen,
  CalendarDays,
  CalendarOff,
  CalendarRange,
  ClipboardCheck,
  FileText,
  Grid3x3,
  GraduationCap,
  HeartPulse,
  LayoutDashboard,
  Network,
  Palmtree,
  Settings,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react"
import { HANDBUCH } from "@/docs/inhalte"
import { ankerId, suchtext } from "@/docs/types"
import type { PermissionSet } from "@/lib/permissions"
import { PLATFORM_APPS } from "@/components/AppSwitcher"

export type CommandGroup = "Seiten" | "Aktionen" | "Mitarbeiter" | "Dokumentation" | "Apps"

export interface Command {
  id: string
  title: string
  href: string
  icon: LucideIcon
  group: CommandGroup
  /** Breadcrumb / Positionsnamen / Kapitel — als Untertitel angezeigt */
  subtitle?: string
  /** Versteckte Suchbegriffe (fließen ins Scoring ein, werden nicht angezeigt) */
  keywords?: string
  /** Sichtbarkeit: nur wenn der Benutzer dieses Set besitzt */
  requiredPermission?: PermissionSet
  /** Alternative zu navigate(href) — für spätere Dialog-Aktionen */
  onSelect?: () => void
}

// ---------------------------------------------------------------------------
// (a) Seiten — 1:1 aus den 13 Routen (App.tsx / Sidebar), mit Suchsynonymen.
//     Nav-Seiten sind in RMNC für alle sichtbar; nur Stammdaten ist admin-only.
// ---------------------------------------------------------------------------
export const pageCommands: Command[] = [
  { id: "page-dashboard", title: "Dashboard", href: "/", icon: LayoutDashboard, group: "Seiten",
    keywords: "start uebersicht home startseite" },
  { id: "page-mitarbeiter", title: "Mitarbeiter", href: "/mitarbeiter", icon: Users, group: "Seiten",
    keywords: "personal verzeichnis mitarbeiterliste personalakte kollegen" },
  { id: "page-anwesenheit", title: "Anwesenheit", href: "/anwesenheit", icon: ClipboardCheck, group: "Seiten",
    keywords: "board ein ausbuchen anwesend praesenz" },
  { id: "page-schichtplan", title: "Schichtplan", href: "/schichtplan", icon: CalendarRange, group: "Seiten",
    keywords: "schicht wochenplan rotation dienstplan frueh spaet nacht" },
  { id: "page-abwesenheiten", title: "Abwesenheiten", href: "/abwesenheiten", icon: CalendarOff, group: "Seiten",
    keywords: "fehlzeiten abwesend abwesenheit" },
  { id: "page-urlaub", title: "Urlaub & Krank", href: "/urlaub", icon: Palmtree, group: "Seiten",
    keywords: "urlaub krank krankmeldung ferien fehlzeit antrag" },
  { id: "page-schulungen", title: "Schulungen", href: "/schulungen", icon: GraduationCap, group: "Seiten",
    keywords: "training weiterbildung unterweisung qualifikation" },
  { id: "page-vorsorge", title: "Vorsorge", href: "/vorsorge", icon: HeartPulse, group: "Seiten",
    keywords: "untersuchung arbeitsmedizin g25 g37 vorsorgeuntersuchung eignung" },
  { id: "page-organisation", title: "Organisation", href: "/organisation", icon: Network, group: "Seiten",
    keywords: "teams stellen struktur abteilung organigramm" },
  { id: "page-dokumente", title: "Dokumente", href: "/dokumente", icon: FileText, group: "Seiten",
    keywords: "dateien arbeitsanweisung anweisung dokument" },
  { id: "page-betriebskalender", title: "Betriebskalender", href: "/betriebskalender", icon: CalendarDays, group: "Seiten",
    keywords: "feiertage betriebsurlaub tagtypen kalender" },
  { id: "page-dokumentation", title: "Dokumentation", href: "/dokumentation", icon: BookOpen, group: "Seiten",
    keywords: "handbuch hilfe anleitung faq" },
  { id: "page-stammdaten", title: "Stammdaten", href: "/admin/stammdaten", icon: Settings, group: "Seiten",
    keywords: "admin lookup grunddaten verwaltung", requiredPermission: "admin" },
]

// ---------------------------------------------------------------------------
// (b) Aktionen — als reine Seiten-Navigation modelliert.
//
//     WICHTIG (Spec-Hinweis, geprüft): Weder /mitarbeiter (EmployeesPage) noch
//     /urlaub (VacationPage) werten aktuell Query-Params aus (kein
//     useSearchParams; VacationPage-Tab ist lokaler useState). Deshalb KEINE
//     ?neu=1 / ?tab=krank-Deeplinks — die Aktionen führen auf die passende
//     Seite. Titel entsprechend gewählt ("… öffnen"). Rollen-gated.
// ---------------------------------------------------------------------------
export const actionCommands: Command[] = [
  { id: "act-neuer-mitarbeiter", title: "Neuen Mitarbeiter anlegen", href: "/mitarbeiter",
    icon: UserPlus, group: "Aktionen", subtitle: "Öffnet das Mitarbeiter-Verzeichnis",
    keywords: "anlegen personal hinzufuegen erstellen neu mitarbeiter",
    requiredPermission: "personal_schreiben" },
  { id: "act-krank-melden", title: "Krankmeldung öffnen", href: "/urlaub",
    icon: HeartPulse, group: "Aktionen", subtitle: "Urlaub & Krank",
    keywords: "krankmeldung au fehlzeit krank melden",
    requiredPermission: "board_nutzen" },
  { id: "act-urlaub", title: "Urlaubsantrag öffnen", href: "/urlaub",
    icon: Palmtree, group: "Aktionen", subtitle: "Urlaub & Krank",
    keywords: "antrag ferien abwesenheit urlaub beantragen",
    requiredPermission: "board_nutzen" },
  { id: "act-wochenplan", title: "Wochenplan öffnen", href: "/schichtplan",
    icon: CalendarRange, group: "Aktionen", subtitle: "Schichtplan",
    keywords: "schichtplan generieren planen wochenplan rotation",
    requiredPermission: "personal_schreiben" },
  { id: "act-betriebskalender", title: "Betriebskalender öffnen", href: "/betriebskalender",
    icon: CalendarDays, group: "Aktionen", subtitle: "Feiertage & Betriebsurlaub",
    keywords: "betriebsurlaub tagtypen feiertage anwenden kalender pflegen",
    requiredPermission: "kalender_pflegen" },
]

// ---------------------------------------------------------------------------
// (c) Dokumentation — Client-Index über HANDBUCH. Kein Netzwerk.
//     Je Abschnitt genau ein Command; Deep-Link /dokumentation#<anker>,
//     der Volltext des Abschnitts fließt als keywords ins Scoring.
// ---------------------------------------------------------------------------
export function buildDocCommands(): Command[] {
  const commands: Command[] = []
  for (const kapitel of HANDBUCH) {
    for (const abschnitt of kapitel.abschnitte) {
      commands.push({
        id: `doc-${kapitel.id}-${abschnitt.id}`,
        title: abschnitt.titel,
        href: `/dokumentation#${ankerId(kapitel.id, abschnitt.id)}`,
        icon: BookOpen,
        group: "Dokumentation",
        subtitle: kapitel.titel,
        keywords: suchtext(abschnitt),
      })
    }
  }
  return commands
}

// ---------------------------------------------------------------------------
// (d) Apps — App-Switcher-Ziele als "Wechseln zu: <App>" (UX_KONZEPT §3.2 Punkt 4).
//     Quelle: dieselbe links.json + PLATFORM_APPS-Liste wie der AppSwitcher.
//     Volle Navigation (window.location.assign) statt SPA-Routing — fremde Origins.
// ---------------------------------------------------------------------------
export function buildAppCommands(
  links: Record<string, string> | null,
  roles: string[],
): Command[] {
  const commands: Command[] = []
  const portalHref = links?.portal ?? (import.meta.env.BASE_URL !== "/" ? "/" : "https://portal.rmnc.app")

  commands.push({
    id: "app-portal",
    title: "Wechseln zu: Portal",
    href: portalHref,
    icon: Grid3x3,
    group: "Apps",
    subtitle: "Alle Anwendungen",
    keywords: "portal home wechseln app",
    onSelect: () => window.location.assign(portalHref),
  })

  for (const app of PLATFORM_APPS) {
    if (app.key === "personal") continue
    if (!app.hasAccess(roles)) continue
    const href = links?.[app.key]
    if (!href) continue
    commands.push({
      id: `app-${app.key}`,
      title: `Wechseln zu: ${app.title}`,
      href,
      icon: Grid3x3,
      group: "Apps",
      subtitle: app.subtitle,
      keywords: `wechseln app ${app.title.toLowerCase()}`,
      onSelect: () => window.location.assign(href),
    })
  }

  return commands
}
